import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { validateMachineImages } from '../utils/machineImageValidator';
import TargetManagement from '../components/TargetManagement';
import { ThemeSelector } from '../components/ThemeSelector';
import { cx } from '../styles';
import { useThemeClasses } from '../providers/ThemeProvider';

const SettingsView = () => {
  const { t } = useTranslation();
  const [imageValidationResult, setImageValidationResult] = useState<any>(null);
  const [isValidatingImages, setIsValidatingImages] = useState(false);
  const themeClasses = useThemeClasses();

  const handleValidateImages = async () => {
    setIsValidatingImages(true);
    try {
      const result = await validateMachineImages();
      setImageValidationResult(result);
    } catch (error) {
      console.error('Image validation failed:', error);
    } finally {
      setIsValidatingImages(false);
    }
  };

  return (
    <div className={cx('flex flex-col h-full py-8 px-6', themeClasses.bgPrimary)}>
      <div className="text-center mb-8">
        <h1 className={cx('text-4xl font-bold text-title mb-4', themeClasses.textPrimary)}>{t('settingsPage.title')}</h1>
        <p className={cx('text-lg', themeClasses.textSecondary)}>{t('settingsPage.description')}</p>
      </div>

      {/* Settings Categories */}
      <div className="max-w-4xl mx-auto w-full space-y-6">
        
        {/* General Settings */}
        <div className={cx('rounded-lg shadow-md p-6', themeClasses.bgSurface, themeClasses.borderPrimary, 'border')}>
          <h2 className={cx('text-xl font-semibold mb-4', themeClasses.textPrimary)}>{t('settingsPage.generalSettings.title')}</h2>
          <div className="space-y-4">
            <div>
              <h3 className={cx('font-medium mb-2', themeClasses.textPrimary)}>{t('settingsPage.generalSettings.theme.title')}</h3>
              <p className={cx('text-sm mb-3', themeClasses.textSecondary)}>{t('settingsPage.generalSettings.theme.description')}</p>
              <div className="flex gap-3">
                <ThemeSelector variant="buttons" showIcons={true} />
              </div>
            </div>
          </div>
        </div>

        {/* Target Management */}
        <TargetManagement className={cx('rounded-lg shadow-md p-6', themeClasses.bgSurface, themeClasses.borderPrimary, 'border')} />

        {/* Developer Tools Section */}
        <div className={cx('rounded-lg shadow-md p-6 border-l-4 border-yellow-400', themeClasses.bgSurface, themeClasses.borderPrimary, 'border')}>
          <h2 className={cx('text-xl font-semibold mb-4', themeClasses.textPrimary)}>{t('settingsPage.developerTools.title')}</h2>
          <div className="space-y-4">
            {/* Machine Image Validation */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className={cx('font-medium mb-2', themeClasses.textPrimary)}>{t('settingsPage.developerTools.machineImageValidation.title')}</h3>
                <p className={cx('text-sm mb-3', themeClasses.textSecondary)}>
                  {t('settingsPage.developerTools.machineImageValidation.description')}
                </p>
              </div>
              <button 
                onClick={handleValidateImages}
                disabled={isValidatingImages}
                className={cx(
                  'px-4 py-2 rounded-md transition-colors font-medium',
                  isValidatingImages 
                    ? cx(themeClasses.bgSuccess, 'opacity-50 cursor-not-allowed', themeClasses.textWhite)
                    : cx(themeClasses.bgSuccess, 'hover:opacity-90', themeClasses.textWhite)
                )}
              >
                {isValidatingImages ? t('settingsPage.developerTools.machineImageValidation.validatingButton') : t('settingsPage.developerTools.machineImageValidation.validateButton')}
              </button>
            </div>
            
            {/* Image Validation Results */}
            {imageValidationResult && (
              <div className={cx('p-4 rounded-md', themeClasses.bgSecondary)}>
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span className={cx('font-medium', themeClasses.textPrimary)}>{t('settingsPage.developerTools.machineImageValidation.totalMachines')}</span>
                    <span className={themeClasses.textPrimary}>{imageValidationResult.total}</span>
                  </div>
                  <div className={cx('flex justify-between text-green-600')}>
                    <span>{t('settingsPage.developerTools.machineImageValidation.imagesFound')}</span>
                    <span>{imageValidationResult.found.length}</span>
                  </div>
                  <div className={cx('flex justify-between text-red-600')}>
                    <span>{t('settingsPage.developerTools.machineImageValidation.imagesMissing')}</span>
                    <span>{imageValidationResult.missing.length}</span>
                  </div>
                  
                  {imageValidationResult.missing.length > 0 && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-red-600 font-medium">
                        {t('settingsPage.developerTools.machineImageValidation.showMissingImages')} ({imageValidationResult.missing.length})
                      </summary>
                      <ul className="mt-2 ml-4 text-xs space-y-1">
                        {imageValidationResult.missing.map((machine: string, index: number) => (
                          <li key={index} className="text-red-500">• {machine}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsView;
